import React, { useState } from 'react';
import { Eye, Car, User, Mail, Phone, Building, FileText, Calendar, Hash, Home, Briefcase, DollarSign, Check, X, Send, MapPin, Target, Clock, CreditCard, UserCheck, FileSignature, Upload, Image } from 'lucide-react';
import { useSupabase } from '../../hooks/useSupabase';
import { abvigAPI } from '../../services/api';
import DataTable from '../../components/Tables/DataTable';
import Modal from '../../components/UI/Modal';
import { formatDate, getStatusColor, getStatusLabel } from '../../utils/helpers';
import { sendEmail } from '../../services/emailService';
import toast from 'react-hot-toast';

export default function VehicleApplications() {
  const { data, refetch } = useSupabase(abvigAPI.getVehicleApplications);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const handleView = (item) => {
    setSelectedApplication(item);
    setIsModalOpen(true);
    setShowEmailForm(false);
    setEmailSubject(`Regarding Your Vehicle Application - ${item.full_name || item.business_name}`);
    setEmailMessage(`Dear ${item.full_name || item.business_name || 'Sir/Madam'},

Thank you for your vehicle application. We have received your request and will review it shortly.

Best regards,
ABVIG Team`);
  };

  const handleStatusUpdate = async (id, status) => {
    setProcessingId(id);
    try {
      await abvigAPI.updateVehicleApplicationStatus(id, status);
      toast.success(`Application ${status} successfully`);
      refetch();
      if (isModalOpen) {
        setIsModalOpen(false);
        setSelectedApplication(null);
        setShowEmailForm(false);
      }
    } catch (error) {
      toast.error(`Failed to ${status} application`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedApplication) return;
    
    setIsSendingEmail(true);
    try {
      await sendEmail({
        to: selectedApplication.email,
        subject: emailSubject,
        message: emailMessage,
        fromName: process.env.REACT_APP_SENDER_NAME || 'ABVIG Team',
      });
      
      toast.success(`✅ Email sent to ${selectedApplication.email}`);
      setShowEmailForm(false);
      setEmailSubject('');
      setEmailMessage('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const renderApplicationDetails = (application) => {
    if (!application) return null;

    if (showEmailForm) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700">Compose Email</h4>
            <button
              onClick={() => setShowEmailForm(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={isSendingEmail}
            >
              Back to Details
            </button>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-gray-600">From:</p>
            <p className="text-sm font-medium text-gray-900">{process.env.REACT_APP_SENDER_NAME || 'ABVIG Team'} &lt;{process.env.REACT_APP_SENDER_EMAIL || 'info@abvig.com'}&gt;</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-sm text-gray-900">{application.email}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter email subject"
              disabled={isSendingEmail}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows="8"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Type your message here..."
              disabled={isSendingEmail}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !emailSubject || !emailMessage}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {isSendingEmail ? 'Sending...' : 'Send Email'}
            </button>
            <button
              onClick={() => setShowEmailForm(false)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSendingEmail}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    const isLease = application.application_type?.toLowerCase() === 'lease';
    const isPurchase = application.application_type?.toLowerCase() === 'purchase';

    return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Car className="w-8 h-8 text-red-600" />
              <div>
                <h3 className="text-lg font-bold text-gray-900">Vehicle Application</h3>
                <p className="text-sm text-gray-600">Application #{application.id}</p>
              </div>
            </div>
            <div className="text-right">
              <span className={`badge ${getStatusColor(application.status)} text-sm`}>
                {getStatusLabel(application.status)}
              </span>
              <p className="text-xs text-gray-500 mt-1">Submitted: {formatDate(application.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Passport Photo */}
        {application.photo_url && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Image className="w-4 h-4" />
                Passport Photo
              </h4>
            </div>
            <div className="p-4 flex justify-center">
              <img 
                src={application.photo_url} 
                alt="Passport" 
                className="max-h-64 rounded-lg border border-gray-200 object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '';
                  e.target.alt = 'Failed to load';
                }}
              />
            </div>
          </div>
        )}

        {/* Applicant Information */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <User className="w-4 h-4" />
              Applicant Information
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <User className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Full Name</p>
                <p className="text-sm font-medium text-gray-900">{application.full_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Building className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Business Name</p>
                <p className="text-sm font-medium text-gray-900">{application.business_name || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{application.email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{application.phone || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Home className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Residential Address</p>
                <p className="text-sm font-medium text-gray-900">{application.residential_address || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Briefcase className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Occupation</p>
                <p className="text-sm font-medium text-gray-900">{application.occupation || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Identification Documents */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Identification Documents
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">ID Type</p>
                <p className="text-sm font-medium text-gray-900">{application.identification_type || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Hash className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">ID Number</p>
                <p className="text-sm font-medium text-gray-900">{application.id_number || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Driver's Licence</p>
                <p className="text-sm font-medium text-gray-900">{application.drivers_licence || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Licence Expiry</p>
                <p className="text-sm font-medium text-gray-900">{application.licence_expiry || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Applicant ID Document */}
        {application.applicant_id_url && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Applicant ID Document
              </h4>
            </div>
            <div className="p-4 flex justify-center">
              <img 
                src={application.applicant_id_url} 
                alt="Applicant ID" 
                className="max-h-64 rounded-lg border border-gray-200 object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '';
                  e.target.alt = 'Failed to load';
                }}
              />
            </div>
          </div>
        )}

        {/* Vehicle Details */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Car className="w-4 h-4" />
              Vehicle Details
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="flex items-start space-x-3">
              <Car className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Application Type</p>
                <p className="text-sm font-medium text-gray-900">
                  {application.application_type === 'lease' ? 'Vehicle Lease' : 
                   application.application_type === 'purchase' ? 'Hire Purchase' : 
                   application.application_type || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Car className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Preferred Vehicle</p>
                <p className="text-sm font-medium text-gray-900">{application.preferred_vehicle || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Car className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Make & Model</p>
                <p className="text-sm font-medium text-gray-900">{application.make_model || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Year</p>
                <p className="text-sm font-medium text-gray-900">{application.year || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Target className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Intended Use</p>
                <p className="text-sm font-medium text-gray-900">{application.intended_use || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Primary Use Area</p>
                <p className="text-sm font-medium text-gray-900">{application.primary_use_area || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">Commencement Date</p>
                <p className="text-sm font-medium text-gray-900">{application.commencement_date || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Lease Details */}
        {isLease && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <FileSignature className="w-4 h-4" />
                Lease Details
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              <div className="flex items-start space-x-3">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Lease Period</p>
                  <p className="text-sm font-medium text-gray-900">{application.lease_period || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Lease Payment</p>
                  <p className="text-sm font-medium text-gray-900">{application.lease_payment || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Payment Frequency</p>
                  <p className="text-sm font-medium text-gray-900">{application.lease_payment_frequency || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Security Deposit</p>
                  <p className="text-sm font-medium text-gray-900">{application.security_deposit || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Maintenance Responsibility</p>
                  <p className="text-sm font-medium text-gray-900">{application.maintenance_responsibility || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Return Date</p>
                  <p className="text-sm font-medium text-gray-900">{application.return_date || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Purchase Details */}
        {isPurchase && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Hire Purchase Details
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              <div className="flex items-start space-x-3">
                <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Purchase Price</p>
                  <p className="text-sm font-medium text-gray-900">{application.purchase_price || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Initial Deposit</p>
                  <p className="text-sm font-medium text-gray-900">{application.initial_deposit || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Balance</p>
                  <p className="text-sm font-medium text-gray-900">{application.balance || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Repayment Period</p>
                  <p className="text-sm font-medium text-gray-900">{application.repayment_period || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Repayment Amount</p>
                  <p className="text-sm font-medium text-gray-900">{application.repayment_amount || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Repayment Frequency</p>
                  <p className="text-sm font-medium text-gray-900">{application.repayment_frequency || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Repayment Source</p>
                  <p className="text-sm font-medium text-gray-900">{application.repayment_source || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Monthly Income</p>
                  <p className="text-sm font-medium text-gray-900">{application.monthly_income || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Guarantor Information */}
        {(application.guarantor_name || application.guarantor_phone) && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                Guarantor Information
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              <div className="flex items-start space-x-3">
                <User className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Guarantor Name</p>
                  <p className="text-sm font-medium text-gray-900">{application.guarantor_name || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Guarantor Phone</p>
                  <p className="text-sm font-medium text-gray-900">{application.guarantor_phone || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Home className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Guarantor Address</p>
                  <p className="text-sm font-medium text-gray-900">{application.guarantor_address || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Briefcase className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Guarantor Occupation</p>
                  <p className="text-sm font-medium text-gray-900">{application.guarantor_occupation || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Guarantor ID Type</p>
                  <p className="text-sm font-medium text-gray-900">{application.guarantor_id_type || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Hash className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Guarantor ID Number</p>
                  <p className="text-sm font-medium text-gray-900">{application.guarantor_id_number || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <User className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Relationship</p>
                  <p className="text-sm font-medium text-gray-900">{application.guarantor_relationship || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Guarantor ID Document */}
        {application.guarantor_id_url && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Guarantor ID Document
              </h4>
            </div>
            <div className="p-4 flex justify-center">
              <img 
                src={application.guarantor_id_url} 
                alt="Guarantor ID" 
                className="max-h-64 rounded-lg border border-gray-200 object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '';
                  e.target.alt = 'Failed to load';
                }}
              />
            </div>
          </div>
        )}

        {/* Signatures */}
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FileSignature className="w-4 h-4" />
              Signatures
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Applicant</p>
              <p className="text-sm font-medium text-gray-900">{application.applicant_name || 'N/A'}</p>
              <p className="text-xs text-gray-400">Signature: {application.applicant_signature || 'N/A'}</p>
              <p className="text-xs text-gray-400">Date: {application.applicant_date || 'N/A'}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Guarantor</p>
              <p className="text-sm font-medium text-gray-900">{application.guarantor_name_signed || 'N/A'}</p>
              <p className="text-xs text-gray-400">Signature: {application.guarantor_signature || 'N/A'}</p>
              <p className="text-xs text-gray-400">Date: {application.guarantor_date || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowEmailForm(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Send className="w-4 h-4" />
            Respond with Email
          </button>

          {application.status === 'pending' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleStatusUpdate(application.id, 'approved')}
                disabled={processingId === application.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {processingId === application.id ? 'Processing...' : 'Approve Application'}
              </button>
              <button
                onClick={() => handleStatusUpdate(application.id, 'rejected')}
                disabled={processingId === application.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-4 h-4" />
                {processingId === application.id ? 'Processing...' : 'Reject Application'}
              </button>
            </div>
          )}

          {application.status !== 'pending' && (
            <div className="pt-2">
              <p className="text-sm text-gray-500 text-center">
                This application has been <span className={`font-semibold ${application.status === 'approved' ? 'text-green-600' : 'text-red-600'}`}>
                  {getStatusLabel(application.status)}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'full_name', label: 'Full Name', sortable: true },
    { key: 'business_name', label: 'Business Name' },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone' },
    { key: 'application_type', label: 'Type', sortable: true },
    { key: 'preferred_vehicle', label: 'Vehicle' },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Date',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending', 'approved', 'rejected']
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Vehicle Applications</h1>
        <p className="text-gray-600">Manage vehicle lease and purchase applications</p>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} applications`}
        actions={actions}
        searchPlaceholder="Search applications..."
        onRowClick={(item) => handleView(item)}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedApplication(null);
          setShowEmailForm(false);
        }}
        title={showEmailForm ? "Compose Email" : "Application Details"}
        size="max-w-3xl"
      >
        {renderApplicationDetails(selectedApplication)}
      </Modal>
    </div>
  );
}